import React from 'react';
import {action, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {UVTemplateLine} from '../../i';
import {uvContentJSON} from './uv-template';
import './uv-print-admin.scss';

const STEP = 0.01;

const round2 = (v: number): number => Math.round(v * 100) / 100;

const arrowStyle: React.CSSProperties = {width: 34, height: 30};

// Frontend preview/authoring helper. The AUTHORITATIVE UV template is now edited
// in the backend FrameParts UV template (EntityTypeID = 20), stored as the
// { "uv_lines": [...] } JSON in Entities.settingsJSON. This panel lets a developer
// paste/preview that array on the product page (?uv_admin), nudge each line's X/Y
// live, and copy the result back out.
@observer
export default class UVPrintAdmin extends React.Component {
    @observable draft = '';
    @observable saveMessage = '';
    // Per-line position tool state. The inputs bind string drafts (not the line
    // numbers) so an in-progress "-" or cleared field isn't clobbered mid-keystroke.
    @observable selLine = 0;
    @observable draftX = '';
    @observable draftY = '';

    constructor(props) {
        super(props);
        makeObservable(this);
        this.draft = JSON.stringify(s.prod && s.prod.uv_lines ? s.prod.uv_lines : [], null, 2);
        this.syncPosDrafts();
    }

    private parseLines(): UVTemplateLine[] {
        const parsed = JSON.parse(this.draft);
        const lines = Array.isArray(parsed) ? parsed : parsed.uv_lines;

        if (!Array.isArray(lines)) {
            throw new Error('Expected an array or { "uv_lines": [...] }');
        }

        return lines;
    }

    private syncPosDrafts() {
        const ln = (s.prod?.uv_lines || [])[this.selLine];
        this.draftX = ln ? String(ln.x ?? 0) : '';
        this.draftY = ln ? String(ln.y ?? 0) : '';
    }

    // Keep the JSON textarea mirroring the live array after position nudges / apply,
    // so "Copy settingsJSON" always captures what the preview shows.
    private refreshDraftFromLines() {
        this.draft = JSON.stringify(s.prod.uv_lines || [], null, 2);
    }

    private redraw() {
        s.ui.doRender = true;
        s.pulse();
    }

    @action
    onDraftChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.draft = e.target.value;
    };

    @action
    applyToPreview = () => {
        this.saveMessage = '';

        try {
            const lines = this.parseLines();

            // Applying a config must not wipe what the shopper/admin already typed into
            // the UV Print on Mat panel: carry each line's entered text (and font pick)
            // forward by index when the incoming config doesn't set its own.
            const prev = s.prod.uv_lines || [];
            for (let i = 0; i < lines.length; i++) {
                const ln = lines[i];
                const old = prev[i];
                if (!ln || !old) continue;
                if (!(ln.value || '').length && (old.value || '').length) ln.value = old.value;
                if (!(ln.font || '').length && (old.font || '').length) ln.font = old.font;
            }

            s.prod.uv_lines = lines;
            s.ps.is_uv_print = true;
            s.ps.uv_print_active = true;
            if (this.selLine >= lines.length) this.selLine = 0;
            this.refreshDraftFromLines();
            this.syncPosDrafts();
            this.redraw();
            this.saveMessage = `Applied ${lines.length} UV line(s) to the preview.`;
        } catch (err) {
            this.saveMessage = `Invalid JSON: ${(err as Error).message}`;
        }
    };

    @action
    copySettingsJSON = async () => {
        this.saveMessage = '';

        try {
            // The template must never bake in preview/calibration text: value is shopper
            // input, and the draft mirrors the live array (typed/seeded values included)
            // after nudges/apply. Always strip it before copying.
            const lines = this.parseLines().map(ln => ({...ln, value: ''}));
            // The template's options ride along so a calibration copy keeps them.
            const json = uvContentJSON(lines, s.prod.uv_options);

            await navigator.clipboard.writeText(json);
            this.saveMessage = 'Copied settingsJSON JSON ({ "uv_lines": [...] }, values cleared) to clipboard.';
        } catch (err) {
            this.saveMessage = `Copy failed: ${(err as Error).message}`;
        }
    };

    @action
    onLineSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.selLine = parseInt(e.target.value, 10) || 0;
        this.syncPosDrafts();
    };

    // Positioning is pointless without visual feedback: a real template loads with
    // uv_print_active false and empty line values, and both render paths hide
    // inactive/empty lines. Activate the preview and seed placeholder text on the
    // line being positioned (copySettingsJSON strips values, so this can't leak
    // into the saved template).
    @action
    private ensureLineVisible(ln: UVTemplateLine) {
        s.ps.is_uv_print = true;
        s.ps.uv_print_active = true;
        if (!((ln.value as string) || '').length) {
            ln.value = ln.title || `Line ${this.selLine + 1}`;
        }
    }

    @action
    nudgeLine = (dx: number, dy: number) => {
        const ln = (s.prod.uv_lines || [])[this.selLine];
        if (!ln) return;
        this.ensureLineVisible(ln);
        if (dx !== 0) {
            ln.x = round2((ln.x || 0) + dx);
            this.draftX = String(ln.x);
        }
        if (dy !== 0) {
            ln.y = round2((ln.y || 0) + dy);
            this.draftY = String(ln.y);
        }
        this.refreshDraftFromLines();
        this.redraw();
    };

    @action
    onAxisChange = (axis: 'x' | 'y', raw: string) => {
        if (axis === 'x') this.draftX = raw;
        else this.draftY = raw;
        const v = parseFloat(raw);
        if (!isFinite(v)) return;
        const ln = (s.prod.uv_lines || [])[this.selLine];
        if (!ln) return;
        this.ensureLineVisible(ln);
        ln[axis] = v;
        this.refreshDraftFromLines();
        this.redraw();
    };

    render() {
        if (!s.ui.is_uv_admin || !s.prod) {
            return null;
        }

        const lines = s.prod.uv_lines || [];

        return (
            <div
                className="gray-box uv-print-admin"
                role="region"
                aria-labelledby="uv-print-admin-title"
            >
                <h2
                    id="uv-print-admin-title"
                    className="uv-print-admin-head"
                >
                    UV Print Admin (uv_lines)
                </h2>
                <form
                    className="uv-print-admin-body"
                    aria-label="UV print uv_lines configuration"
                    onSubmit={e => e.preventDefault()}
                >
                    {/* Line Position sits first so the adjuster stays near the product image
                        while nudging - the JSON textarea below would otherwise push it out
                        of view. */}
                    {lines.length > 0 && (
                        <div style={{borderBottom: '1px dashed #ccc', marginBottom: 12, paddingBottom: 10}}>
                            <h3 style={{fontSize: 15, margin: '0 0 6px 0'}}>Line Position</h3>
                            <p style={{fontSize: 12, lineHeight: 1.4, margin: '0 0 8px 0'}}>
                                Nudges the selected line's x/y in inches (0.01 per click) and updates the JSON below. Note the coordinate space differs slightly per customization style: new style (2) positions relative to the mat/document; old (1) and none (0) position from the frame photo's center.
                            </p>
                            <div style={{display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap'}}>
                                <select
                                    aria-label="UV line to position"
                                    value={this.selLine}
                                    onChange={this.onLineSelect}
                                >
                                    {lines.map((ln, i) => (
                                        <option
                                            key={i}
                                            value={i}
                                        >
                                            {`Line ${i + 1}${ln && ln.title ? ` - ${ln.title}` : ''}`}
                                        </option>
                                    ))}
                                </select>
                                <label style={{display: 'flex', alignItems: 'center', gap: 4, margin: 0}}>
                                    X
                                    <input
                                        type="number"
                                        step={STEP}
                                        style={{width: 90}}
                                        value={this.draftX}
                                        onChange={e => this.onAxisChange('x', e.target.value)}
                                        aria-label="UV line X position in inches"
                                    />
                                </label>
                                <label style={{display: 'flex', alignItems: 'center', gap: 4, margin: 0}}>
                                    Y
                                    <input
                                        type="number"
                                        step={STEP}
                                        style={{width: 90}}
                                        value={this.draftY}
                                        onChange={e => this.onAxisChange('y', e.target.value)}
                                        aria-label="UV line Y position in inches"
                                    />
                                </label>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: 2, justifyItems: 'center'}}>
                                    <span />
                                    <button
                                        type="button"
                                        style={arrowStyle}
                                        onClick={() => this.nudgeLine(0, -STEP)}
                                        aria-label="Move line up 0.01 inches"
                                    >
                                        ▲
                                    </button>
                                    <span />
                                    <button
                                        type="button"
                                        style={arrowStyle}
                                        onClick={() => this.nudgeLine(-STEP, 0)}
                                        aria-label="Move line left 0.01 inches"
                                    >
                                        ◀
                                    </button>
                                    <span />
                                    <button
                                        type="button"
                                        style={arrowStyle}
                                        onClick={() => this.nudgeLine(STEP, 0)}
                                        aria-label="Move line right 0.01 inches"
                                    >
                                        ▶
                                    </button>
                                    <span />
                                    <button
                                        type="button"
                                        style={arrowStyle}
                                        onClick={() => this.nudgeLine(0, STEP)}
                                        aria-label="Move line down 0.01 inches"
                                    >
                                        ▼
                                    </button>
                                    <span />
                                </div>
                            </div>
                        </div>
                    )}

                    <p className="uv-print-admin-note">
                        The authoritative UV template is edited in the backend FrameParts UV template (EntityTypeID = 20). This panel edits the in-page <code>uv_lines</code> array for preview only.
                    </p>
                    <textarea
                        className="uv-print-admin-json"
                        aria-label="UV lines JSON"
                        rows={16}
                        value={this.draft}
                        onChange={this.onDraftChange}
                        spellCheck={false}
                    />
                    <div className="uv-print-admin-actions">
                        <button
                            type="button"
                            onClick={this.applyToPreview}
                            aria-label="Apply UV lines JSON to preview"
                        >
                            Apply to preview
                        </button>
                        <button
                            type="button"
                            onClick={this.copySettingsJSON}
                            aria-label="Copy settingsJSON JSON to clipboard"
                        >
                            Copy settingsJSON
                        </button>
                        <div
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="uv-print-admin-save-msg"
                        >
                            {this.saveMessage}
                        </div>
                    </div>
                </form>
            </div>
        );
    }
}
